import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { GameState } from './gameStore'

// 存档槽位信息
export interface SaveSlotInfo {
  id: string
  name: string
  createdAt: number
  updatedAt: number
  playTimeSeconds: number
  gameYear: number
  gameMonth: number
  preview: {
    totalPopulation: number
    totalMoney: number
    totalFood: number
    talentCount: number
    buildingCount: number
  }
  gameData: Partial<GameState>
}

// 自动存档数据
export interface AutoSaveData {
  lastSaveAt: number
  gameData: Partial<GameState>
}

// 存档状态
export interface SaveState {
  // 自动存档
  autoSave: AutoSaveData | null

  // 3 个存档槽位
  slots: [SaveSlotInfo | null, SaveSlotInfo | null, SaveSlotInfo | null]

  // 游戏时长（秒）
  totalPlayTime: number
  sessionStartTime: number | null

  // 自动存档配置
  autoSaveInterval: number // 毫秒
  lastAutoSaveAt: number

  // 操作
  initSession: () => void
  updatePlayTime: () => void
  autoSaveGame: (gameState: Partial<GameState>) => void
  saveToSlot: (slotIndex: number, name: string, gameState: Partial<GameState>) => boolean
  loadFromSlot: (slotIndex: number) => SaveSlotInfo | null
  deleteSlot: (slotIndex: number) => void
  loadAutoSave: () => AutoSaveData | null
  getSlotPreview: (slotIndex: number) => SaveSlotInfo | null
}

// 生成唯一 ID
const generateId = () => Math.random().toString(36).substring(2, 15)

// 提取游戏数据用于存档
const extractGameData = (gameState: Partial<GameState>): Partial<GameState> => ({
  currentView: gameState.currentView,
  isPaused: gameState.isPaused,
  gameSpeed: gameState.gameSpeed,
  gameDate: gameState.gameDate,
  resources: gameState.resources,
  resourceIncome: gameState.resourceIncome,
  buildings: gameState.buildings,
  populationAllocation: gameState.populationAllocation,
  talents: gameState.talents,
  availableTalents: gameState.availableTalents,
  recruitCooldown: gameState.recruitCooldown,
})

// 提取预览信息
const extractPreview = (gameState: Partial<GameState>) => ({
  totalPopulation: gameState.resources?.population || 0,
  totalMoney: gameState.resources?.money || 0,
  totalFood: gameState.resources?.food || 0,
  talentCount: gameState.talents?.length || 0,
  buildingCount: gameState.buildings?.filter(b => b.level > 0).length || 0,
})

export const useSaveStore = create<SaveState>()(
  persist(
    (set, get) => ({
      // 初始状态
      autoSave: null,
      slots: [null, null, null],
      totalPlayTime: 0,
      sessionStartTime: null,
      autoSaveInterval: 30000, // 30 秒
      lastAutoSaveAt: 0,

      // 初始化游戏会话
      initSession: () => {
        set({
          sessionStartTime: Date.now(),
          lastAutoSaveAt: Date.now(),
        })
      },

      // 更新游戏时长
      updatePlayTime: () => {
        const { sessionStartTime, totalPlayTime } = get()
        if (!sessionStartTime) return

        const sessionDuration = Math.floor((Date.now() - sessionStartTime) / 1000)
        set({
          totalPlayTime: totalPlayTime + sessionDuration,
          sessionStartTime: Date.now(),
        })
      },

      // 自动存档
      autoSaveGame: (gameState) => {
        const now = Date.now()
        const { autoSaveInterval, lastAutoSaveAt } = get()

        // 检查是否达到自动存档间隔
        if (now - lastAutoSaveAt < autoSaveInterval) return

        const gameData = extractGameData(gameState)

        set({
          autoSave: {
            lastSaveAt: now,
            gameData,
          },
          lastAutoSaveAt: now,
        })
      },

      // 保存到指定槽位
      saveToSlot: (slotIndex, name, gameState) => {
        if (slotIndex < 0 || slotIndex > 2) return false

        const now = Date.now()
        const { totalPlayTime, sessionStartTime } = get()
        const sessionDuration = sessionStartTime
          ? Math.floor((now - sessionStartTime) / 1000)
          : 0
        const currentPlayTime = totalPlayTime + sessionDuration

        const gameData = extractGameData(gameState)
        const preview = extractPreview(gameState)

        const slotInfo: SaveSlotInfo = {
          id: generateId(),
          name: name || `存档 ${slotIndex + 1}`,
          createdAt: now,
          updatedAt: now,
          playTimeSeconds: currentPlayTime,
          gameYear: gameState.gameDate?.year || -770,
          gameMonth: gameState.gameDate?.month || 1,
          preview,
          gameData,
        }

        set((state) => {
          const newSlots = [...state.slots] as [SaveSlotInfo | null, SaveSlotInfo | null, SaveSlotInfo | null]
          newSlots[slotIndex] = slotInfo
          return { slots: newSlots }
        })

        return true
      },

      // 从槽位读取
      loadFromSlot: (slotIndex) => {
        if (slotIndex < 0 || slotIndex > 2) return null
        return get().slots[slotIndex]
      },

      // 删除槽位
      deleteSlot: (slotIndex) => {
        if (slotIndex < 0 || slotIndex > 2) return

        set((state) => {
          const newSlots = [...state.slots] as [SaveSlotInfo | null, SaveSlotInfo | null, SaveSlotInfo | null]
          newSlots[slotIndex] = null
          return { slots: newSlots }
        })
      },

      // 加载自动存档
      loadAutoSave: () => {
        return get().autoSave
      },

      // 获取槽位预览
      getSlotPreview: (slotIndex) => {
        if (slotIndex < 0 || slotIndex > 2) return null
        return get().slots[slotIndex]
      },
    }),
    {
      name: 'huaxia-chronicles-save-storage',
      partialize: (state) => ({
        autoSave: state.autoSave,
        slots: state.slots,
        totalPlayTime: state.totalPlayTime,
        autoSaveInterval: state.autoSaveInterval,
      }),
    }
  )
)
